import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getUniversityBySlug } from '../data/universityData';
import { CheckCircleIcon, ChevronLeftIcon, BuildingIcon, GraduationCapIcon, AwardIcon, PhoneIcon, LockIcon, ArrowLeftRightIcon, FileCheckIcon, TimerIcon, HelpCircleIcon } from 'lucide-react';
const iconMap: Record<string, React.ElementType> = {
  ArrowLeftRightIcon,
  FileCheckIcon,
  TimerIcon,
  AwardIcon
};
export function BrandedCollegePage() {
  const {
    collegeSlug
  } = useParams<{
    collegeSlug: string;
  }>();
  const navigate = useNavigate();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const totalSteps = 4;
  const [formData, setFormData] = useState({
    degreeLevel: '',
    program: '',
    militaryAffiliation: '',
    highestEducation: '',
    hsGradYear: '',
    startDate: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false
  });
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  useEffect(() => {
    // Auto-lookup zip code
    if (formData.zipCode.length === 5) {
      fetch(`https://api.zippopotam.us/us/${formData.zipCode}`).then(res => {
        if (res.ok) return res.json();
        throw new Error('Invalid zip');
      }).then(data => {
        if (data.places && data.places.length > 0) {
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      }).catch(() => {
        // Handle error silently
      });
    }
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gray-50" data-id="element-502">
        <div className="text-center" data-id="element-503">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-504">
            University Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-505">
            We couldn't find the college you're looking for.
          </p>
          <button onClick={() => navigate('/')} className="bg-[#3b82f6] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#2563eb] transition-colors" data-id="element-506">
            Return Home
          </button>
        </div>
      </div>;
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setFormData(prev => ({
      ...prev,
      zipCode: value
    }));
  };
  const nextStep = () => {
    setDirection(1);
    setStep(s => Math.min(s + 1, totalSteps));
  };
  const prevStep = () => {
    setDirection(-1);
    setStep(s => Math.max(s - 1, 1));
  };
  const handleSubmit = () => {
    if (formData.consent) {
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    }
  };
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // Filter programs based on selected degree level
  const filteredPrograms = university.programs.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel);
  // Group programs by category for the bottom section
  const programsByCategory = university.programs.reduce((acc, program) => {
    if (!acc[program.category]) {
      acc[program.category] = [];
    }
    acc[program.category].push(program.name);
    return acc;
  }, {} as Record<string, string[]>);
  if (isSubmitted) {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-507">
        {/* Top Header Bar */}
        <div className="w-full bg-white border-b border-gray-200 py-4 px-4 flex justify-between items-center max-w-7xl mx-auto" data-id="element-508">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} className="h-10 object-contain" data-id="element-509" /> : <div className="text-xl font-bold text-gray-900" data-id="element-510">
              {university.logoText}
            </div>}
          {university.phoneNumber && <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-511">
              <PhoneIcon className="w-5 h-5" data-id="element-512" />
              <span data-id="element-513">{university.phoneNumber}</span>
            </div>}
        </div>
        <div className="flex-grow flex items-center justify-center p-4" data-id="element-514">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center" data-id="element-515">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-516">
              <CheckCircleIcon className="w-10 h-10 text-green-500" data-id="element-517" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-518">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-6 text-lg" data-id="element-519">
              Your request for information about{' '}
              <strong data-id="element-520">{formData.program}</strong> at{' '}
              <strong data-id="element-521">{university.name}</strong> has been submitted.
            </p>
            <p className="text-gray-600 mb-8" data-id="element-522">
              A representative from {university.name} will contact you shortly
              to discuss your educational goals.
            </p>

            <button onClick={() => navigate('/')} className="w-full py-3.5 text-white font-bold rounded-full text-lg transition-opacity hover:opacity-90" style={{
            backgroundColor: university.ctaColor
          }} data-id="element-523">
              Return Home
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white font-sans" data-id="element-524">
      {/* Header Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-3 px-4 md:px-8 flex justify-between items-center" data-id="element-525">
        {university.logoUrl ? <img src={university.logoUrl} alt={university.name} className="h-10 object-contain" data-id="element-526" /> : <div className="text-xl font-bold text-gray-900" data-id="element-527">
            {university.logoText}
          </div>}
        {university.phoneNumber && <div className="flex items-center gap-1.5 text-gray-600 font-medium text-lg" data-id="element-528">
            <PhoneIcon className="w-5 h-5" data-id="element-529" />
            <span data-id="element-530">{university.phoneNumber}</span>
          </div>}
      </div>

      {/* Hero Section */}
      <div className="relative w-full min-h-[600px] flex items-center" data-id="element-531">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url(${university.heroImageUrl})`
      }} data-id="element-532" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-12 md:py-20 grid lg:grid-cols-2 gap-8 items-center" data-id="element-533">
          {/* Left: Tagline */}
          <div className="hidden lg:block" data-id="element-534">
            <div className="bg-black/60 p-6 inline-block" data-id="element-535">
              <h1 className="text-white text-4xl md:text-5xl font-serif italic leading-tight max-w-md" data-id="element-536">
                {university.heroTagline}
              </h1>
            </div>
          </div>

          {/* Right: Form Card */}
          <div className="bg-white shadow-2xl w-full max-w-md mx-auto lg:ml-auto lg:mr-0 flex flex-col" data-id="element-537">
            {/* Form Header */}
            <div className="bg-[#1a1a1a] text-center py-6 px-4" data-id="element-538">
              <div className="text-red-600 font-bold tracking-wider text-sm mb-1 uppercase" data-id="element-539">
                Classes Start {university.classesStartDate}
              </div>
              <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-wide" data-id="element-540">
                Take the First Step Today
              </h2>
            </div>

            {/* Step Indicators - 4 steps */}
            <div className="flex w-full" data-id="element-541">
              {[{
              num: 1,
              label: 'Program',
              sub: 'Selection'
            }, {
              num: 2,
              label: 'Education',
              sub: 'Details'
            }, {
              num: 3,
              label: 'Location',
              sub: 'Info'
            }, {
              num: 4,
              label: 'Contact',
              sub: 'Info'
            }].map(s => <div key={s.num} className={`flex-1 py-2.5 px-1 flex items-center justify-center gap-1.5 ${step === s.num ? 'bg-black text-white' : step > s.num ? 'bg-gray-300 text-gray-700' : 'bg-gray-200 text-gray-500'}`} data-id="element-542">
                  <div className={`w-6 h-6 flex items-center justify-center border-2 text-sm font-bold ${step === s.num ? 'border-white' : step > s.num ? 'border-gray-500' : 'border-gray-400'}`} data-id="element-543">
                    {step > s.num ? '✓' : s.num}
                  </div>
                  <div className="text-[10px] leading-tight hidden sm:block" data-id="element-544">
                    <span className="font-bold block uppercase" data-id="element-545">{s.label}</span>
                    <span className="capitalize" data-id="element-546">{s.sub}</span>
                  </div>
                </div>)}
            </div>

            {/* Form Body */}
            <div className="p-6 md:p-8 bg-[#f5f4f0] flex-grow" data-id="element-547">
              <p className="text-gray-600 italic text-sm mb-4" data-id="element-548">
                All fields required
              </p>

              <div className="overflow-hidden" data-id="element-549">
                <AnimatePresence mode="wait" custom={direction} data-id="element-550">
                  {/* Step 1: Program Selection */}
                  {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-551">
                      <div data-id="element-552">
                        <select name="degreeLevel" value={formData.degreeLevel} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-553">
                          <option value="" data-id="element-554">Select a Degree Level*</option>
                          {university.degreeLevels.map((level, i) => <option key={i} value={level} data-id="element-555">
                              {level}
                            </option>)}
                        </select>
                      </div>

                      <div data-id="element-556">
                        <select name="program" value={formData.program} onChange={handleInputChange} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-557">
                          <option value="" data-id="element-558">Choose your degree program*</option>
                          {filteredPrograms.map((p, i) => <option key={i} value={p.name} data-id="element-559">
                              {p.name}
                            </option>)}
                        </select>
                      </div>

                      <div className="pt-2" data-id="element-560">
                        <label className="flex items-center gap-1 text-sm font-bold text-gray-900 mb-2" data-id="element-561">
                          *Are You Affiliated with the Military?
                          <HelpCircleIcon className="w-4 h-4 text-gray-500" data-id="element-562" />
                        </label>
                        <div className="flex gap-6" data-id="element-563">
                          <label className="flex items-center gap-2 cursor-pointer" data-id="element-564">
                            <input type="radio" name="militaryAffiliation" value="Yes" checked={formData.militaryAffiliation === 'Yes'} onChange={handleInputChange} className="w-5 h-5 text-red-600 focus:ring-red-600" data-id="element-565" />
                            <span className="text-gray-900 font-medium" data-id="element-566">
                              Yes
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer" data-id="element-567">
                            <input type="radio" name="militaryAffiliation" value="No" checked={formData.militaryAffiliation === 'No'} onChange={handleInputChange} className="w-5 h-5 text-red-600 focus:ring-red-600" data-id="element-568" />
                            <span className="text-gray-900 font-medium" data-id="element-569">
                              No
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="pt-6" data-id="element-570">
                        <button onClick={nextStep} type="button" disabled={!formData.degreeLevel || !formData.program || !formData.militaryAffiliation} className="w-full py-4 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-571">
                          Go To Next Step
                        </button>
                        <div className="flex items-center justify-center gap-2 mt-4 text-gray-600 text-sm" data-id="element-572">
                          <LockIcon className="w-4 h-4" data-id="element-573" />
                          <span data-id="element-574">We respect your privacy</span>
                        </div>
                      </div>
                    </motion.div>}

                  {/* Step 2: Education Details */}
                  {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-575">
                      <div data-id="element-576">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-577">
                          Highest Level of Education*
                        </label>
                        <select name="highestEducation" value={formData.highestEducation} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-578">
                          <option value="" data-id="element-579">Please Select</option>
                          <option value="Some High School" data-id="element-580">
                            Some High School
                          </option>
                          <option value="High School/GED" data-id="element-581">
                            High School/GED
                          </option>
                          <option value="Some College" data-id="element-582">Some College</option>
                          <option value="Associate's Degree" data-id="element-583">
                            Associate's Degree
                          </option>
                          <option value="Bachelor's Degree" data-id="element-584">
                            Bachelor's Degree
                          </option>
                          <option value="Master's Degree" data-id="element-585">
                            Master's Degree
                          </option>
                          <option value="Doctorate" data-id="element-586">Doctorate</option>
                        </select>
                      </div>

                      <div data-id="element-587">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-588">
                          High School Graduation Year*
                        </label>
                        <select name="hsGradYear" value={formData.hsGradYear} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-589">
                          <option value="" data-id="element-590">Please Select</option>
                          {years.map(year => <option key={year} value={year} data-id="element-591">
                              {year}
                            </option>)}
                        </select>
                      </div>

                      <div data-id="element-592">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-593">
                          When Would You Like to Start?*
                        </label>
                        <select name="startDate" value={formData.startDate} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-594">
                          <option value="" data-id="element-595">Please Select</option>
                          <option value="Immediately" data-id="element-596">Immediately</option>
                          <option value="1-3 Months" data-id="element-597">1-3 Months</option>
                          <option value="3-6 Months" data-id="element-598">3-6 Months</option>
                          <option value="6-12 Months" data-id="element-599">6-12 Months</option>
                          <option value="1+ Year" data-id="element-600">1+ Year</option>
                        </select>
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-601">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-602">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-603" />
                          Back
                        </button>
                        <button onClick={nextStep} type="button" disabled={!formData.highestEducation || !formData.hsGradYear || !formData.startDate} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-604">
                          Next Step
                        </button>
                      </div>
                    </motion.div>}

                  {/* Step 3: Location */}
                  {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-605">
                      <div data-id="element-606">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-607">
                          Zip Code*
                        </label>
                        <input type="text" inputMode="numeric" name="zipCode" value={formData.zipCode} onChange={handleZipChange} maxLength={5} placeholder="e.g. 90210" className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-608" />
                      </div>

                      <div className="flex gap-4" data-id="element-609">
                        <div className="flex-1" data-id="element-610">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-611">
                            City*
                          </label>
                          <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-612" />
                        </div>
                        <div className="flex-1" data-id="element-613">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-614">
                            State*
                          </label>
                          <input type="text" name="state" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-615" />
                        </div>
                      </div>

                      <div data-id="element-616">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-617">
                          Street Address{' '}
                          <span className="text-gray-400 normal-case font-normal" data-id="element-618">
                            (optional)
                          </span>
                        </label>
                        <input type="text" name="streetAddress" value={formData.streetAddress} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-619" />
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-620">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-621">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-622" />
                          Back
                        </button>
                        <button onClick={nextStep} type="button" disabled={!formData.zipCode || !formData.city || !formData.state} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-623">
                          Next Step
                        </button>
                      </div>
                    </motion.div>}

                  {/* Step 4: Contact Info */}
                  {step === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-624">
                      <div className="flex gap-4" data-id="element-625">
                        <div className="flex-1" data-id="element-626">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-627">
                            First Name*
                          </label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-628" />
                        </div>
                        <div className="flex-1" data-id="element-629">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-630">
                            Last Name*
                          </label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-631" />
                        </div>
                      </div>

                      <div data-id="element-632">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-633">
                          Email Address*
                        </label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-634" />
                      </div>

                      <div data-id="element-635">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-636">
                          Phone Number*
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-637" />
                      </div>

                      <div className="flex items-start gap-3 mt-4" data-id="element-638">
                        <input type="checkbox" id="consent" name="consent" checked={formData.consent} onChange={handleInputChange} className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-600" data-id="element-639" />
                        <label htmlFor="consent" className="text-[10px] text-gray-600 leading-tight" data-id="element-640">
                          By clicking the Request Info button, you agree to be
                          contacted by phone, text message, or email via
                          automated systems by {university.name} about your
                          education at the phone numbers you provided above,
                          including any wireless number(s). You are not
                          obligated to agree to automated contact to enroll.
                          Note that even non-automated calls are recorded for
                          quality assurance.
                        </label>
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-641">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-642">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-643" />
                          Back
                        </button>
                        <button onClick={handleSubmit} type="button" disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.consent} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-644">
                          Request Info
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Row */}
      <div className="bg-[#f5f5f5] py-16 border-b border-gray-200" data-id="element-645">
        <div className="max-w-7xl mx-auto px-4" data-id="element-646">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-647">
            {university.features?.map((feature, idx) => {
            const Icon = iconMap[feature.icon] || CheckCircleIcon;
            return <div key={idx} className="flex flex-col items-center text-center" data-id="element-648">
                  <div className="mb-6" data-id="element-649">
                    <Icon className="w-16 h-16 text-gray-700 stroke-1" data-id="element-650" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-black mb-3" data-id="element-651">
                    {feature.label}
                    {feature.superscript && <sup className="text-xs ml-0.5" data-id="element-652">
                        {feature.superscript}
                      </sup>}
                  </h3>
                  <p className="text-gray-700 text-sm" data-id="element-653">
                    {feature.description}
                    {feature.superscript && <sup className="text-[10px] ml-0.5" data-id="element-654">
                        {feature.superscript}
                      </sup>}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      {university.testimonial && <div className="w-full py-20 px-4" style={{
      backgroundColor: university.primaryColor
    }} data-id="element-655">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-8 items-start" data-id="element-656">
            <div className="text-white text-9xl font-serif leading-none h-20 opacity-90" data-id="element-657">
              "
            </div>
            <div className="flex-1 pt-4" data-id="element-658">
              <p className="text-white text-xl md:text-2xl font-serif italic leading-relaxed mb-6" data-id="element-659">
                {university.testimonial.quote}
              </p>
              <div className="text-right" data-id="element-660">
                <div className="text-white font-bold text-lg" data-id="element-661">
                  {university.testimonial.name}
                </div>
                <div className="text-white/90 italic text-sm" data-id="element-662">
                  {university.testimonial.program}
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Mobile/Access Section */}
      {university.mobileSection && <div className="w-full py-20 bg-white" data-id="element-663">
          <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center" data-id="element-664">
            <div data-id="element-665">
              <img src="https://images.unsplash.com/photo-1512314889357-e157c22f938d?w=800&h=600&fit=crop&q=80" alt="Student using mobile phone" className="w-full h-auto shadow-lg" data-id="element-666" />
            </div>
            <div className="space-y-6" data-id="element-667">
              <h2 className="text-3xl md:text-4xl font-serif text-[#991B1B]" data-id="element-668">
                {university.mobileSection.heading}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-669">
                {university.mobileSection.description}
              </p>
              <button className="px-8 py-3 text-white font-bold text-lg transition-colors border-2 hover:bg-white hover:text-[#B91C1C]" style={{
            backgroundColor: university.ctaColor,
            borderColor: university.ctaColor
          }} onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} data-id="element-670">
                Request Info
              </button>
            </div>
          </div>
        </div>}

      {/* University Details */}
      <div className="max-w-7xl mx-auto px-4 py-16 border-t border-gray-200" data-id="element-671">
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-672">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-673">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-674">
              <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-675" />
            </div>
            <div data-id="element-676">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-677">
                Institution Type
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-678">
                {university.type}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-679">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-680">
              <AwardIcon className="w-6 h-6 text-green-600" data-id="element-681" />
            </div>
            <div data-id="element-682">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-683">
                Accreditation
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-684">
                {university.accreditation}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-685">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-686">
              <GraduationCapIcon className="w-6 h-6 text-purple-600" data-id="element-687" />
            </div>
            <div data-id="element-688">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-689">
                Founded
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-690">
                {university.founded}
              </p>
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-691">
          Available Online Programs
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-692">
          {Object.entries(programsByCategory).map(([category, programs]) => <div key={category} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-693">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-694">
                {category}
              </h3>
              <ul className="space-y-2" data-id="element-695">
                {programs.map((program, idx) => <li key={idx} className="flex items-start gap-2 text-gray-700" data-id="element-696">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" data-id="element-697" />
                    <span data-id="element-698">{program}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>

      {/* Footer Separator */}
      <div className="w-full h-1 bg-[#991B1B]" data-id="element-699"></div>
    </div>;
}